'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { BurgerMenuNavigationLoaderData } from './burger-menu-loader-data';
import { hydrateContentSlotPageComponentData, type ContentSlotPageWithComponentData } from './content-slot-page';
import { parseCategorySubmenuSlots, type CategorySubmenuSlots } from './category-submenu-slots';
import { parseMenuItemListOverrides, type MenuItemListOverrides } from './menu-item-list-overrides';

export const BURGER_MENU_NAVIGATION_RESOURCE = '/resource/burger-menu-navigation';

type UseBurgerMenuContentResult = {
    subCategoriesPromise: Promise<ShopperProducts.schemas['Category'][]> | undefined;
    contentSlots: ContentSlotPageWithComponentData | undefined;
    menuOverrides: MenuItemListOverrides;
    categorySubmenuSlots: CategorySubmenuSlots;
    isContentSlotsLoading: boolean;
    hasLoaded: boolean;
};

export function useBurgerMenuContent(open: boolean): UseBurgerMenuContentResult {
    const fetcher = useFetcher<BurgerMenuNavigationLoaderData>({ key: 'burger-menu-navigation' });
    const subsDeferredResolveRef = useRef<((subs: ShopperProducts.schemas['Category'][]) => void) | null>(null);
    const [subCategoriesPromise, setSubCategoriesPromise] = useState<
        Promise<ShopperProducts.schemas['Category'][]> | undefined
    >();

    useEffect(() => {
        if (!open || fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        void fetcher.load(BURGER_MENU_NAVIGATION_RESOURCE);
    }, [open, fetcher, fetcher.data, fetcher.state, fetcher.load]);

    useEffect(() => {
        if (fetcher.data?.subs && subsDeferredResolveRef.current) {
            subsDeferredResolveRef.current(fetcher.data.subs);
            subsDeferredResolveRef.current = null;
        }
    }, [fetcher.data?.subs]);

    useEffect(() => {
        if (!open) {
            setSubCategoriesPromise(undefined);
            subsDeferredResolveRef.current = null;
            return;
        }

        if (fetcher.data?.subs) {
            setSubCategoriesPromise(Promise.resolve(fetcher.data.subs));
            subsDeferredResolveRef.current = null;
            return;
        }

        setSubCategoriesPromise(
            new Promise<ShopperProducts.schemas['Category'][]>((resolve) => {
                subsDeferredResolveRef.current = resolve;
            })
        );
    }, [open, fetcher.data?.subs]);

    const isContentSlotsLoading = open && !fetcher.data;
    const contentSlots = useMemo(
        () => (fetcher.data?.contentSlots ? hydrateContentSlotPageComponentData(fetcher.data.contentSlots) : undefined),
        [fetcher.data?.contentSlots]
    );

    const menuOverrides = useMemo(() => (contentSlots ? parseMenuItemListOverrides(contentSlots) : {}), [contentSlots]);

    const categorySubmenuSlots = useMemo(
        () => (contentSlots ? parseCategorySubmenuSlots(contentSlots) : {}),
        [contentSlots]
    );

    return useMemo(
        () => ({
            subCategoriesPromise,
            contentSlots,
            menuOverrides,
            categorySubmenuSlots,
            isContentSlotsLoading,
            hasLoaded: Boolean(fetcher.data),
        }),

        [subCategoriesPromise, contentSlots, menuOverrides, categorySubmenuSlots, isContentSlotsLoading, fetcher.data]
    );
}
